'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { persistAuthStorage, setLoginRedirectHandled, isLoginRedirectHandled } from '../../lib/authStorage';
import Card from '../../components/Card';
import Alert from '../../components/Alert';
import LoadingOverlay from '../../components/LoadingOverlay';
import { useTranslation } from '../../lib/i18n';

type LoginRequest = {
  email: string;
  password: string;
};

type AuthResponse = {
  id?: number;
  token: string;
  tokenExp?: number | null;
  impersonatedBy?: string | null;
  email: string;
  role: string;
  roles?: string[];
  permissions?: string[];
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
  firstName?: string | null;
  lastName?: string | null;
  mustChangePassword?: boolean;
};

type VerifyTwoFactorRequest = {
  twoFactorToken: string;
  code: string;
};

export default function Login() {
  const router = useRouter();
  const { t } = useTranslation();
  const { isAuthenticated, mounted } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [canSubmit, setCanSubmit] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Read redirect target from URL (?redirect=...) set by middleware
  // and store it in sessionStorage so finishLogin can pick it up.
  // Also prefill email from localStorage — must run after hydration to avoid
  // SSR/client mismatch (server has no window, so initial state is always '').
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedEmail = window.localStorage.getItem('lastLoginEmail');
    if (savedEmail) setEmail(savedEmail);
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      sessionStorage.setItem('redirectAfterLogin', redirect);
    }
  }, []);

  // Focus email input after hydration — cursor at end of prefilled email.
  // Runs only once when the form becomes visible (mounted, not authenticated,
  // not in 2FA step). Uses rAF to ensure the input is rendered + email is set.
  useEffect(() => {
    if (!mounted || isAuthenticated || twoFactorToken) return;
    const id = requestAnimationFrame(() => {
      const input = emailInputRef.current;
      if (!input || document.activeElement === input) return;
      input.focus();
      // Place cursor at end of any prefilled email (guard against
      // InvalidStateError if the input loses focus between frames)
      try {
        const len = input.value.length;
        input.setSelectionRange(len, len);
      } catch {}
    });
    return () => cancelAnimationFrame(id);
  }, [mounted, isAuthenticated, twoFactorToken]);

  // If already authenticated (e.g. user navigates to /login manually),
  // redirect to the stored target or home — don't show the login form.
  // Skip if finishLogin() already handled the redirect (loginRedirectHandled flag).
  useEffect(() => {
    if (mounted && isAuthenticated) {
      if (isLoginRedirectHandled()) {
        // finishLogin already set the redirect — don't override.
        // Reset the flag so it doesn't block future navigations.
        setLoginRedirectHandled(false);
        return;
      }
      const redirect = sessionStorage.getItem('redirectAfterLogin');
      if (redirect) {
        sessionStorage.removeItem('redirectAfterLogin');
        router.replace(redirect);
      } else {
        router.replace('/');
      }
    }
  }, [mounted, isAuthenticated, router]);

  // Inline Validation states
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [twoFactorCodeTouched, setTwoFactorCodeTouched] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPassword = password.length >= 6;
  const isValidTwoFactorCode = /^\d{6}$/.test(twoFactorCode);

  // Form is submittable only when all required fields are filled and valid
  const formValid = twoFactorToken ? isValidTwoFactorCode : (isValidEmail && isValidPassword);

  const finishLogin = (response: AuthResponse, loginEmail?: string, loginPassword?: string) => {
    // Set flag BEFORE persistAuthStorage to prevent "already authenticated" effects
    // in Layout.tsx and login/page.tsx from overriding our redirect.
    // persistAuthStorage → checkAuth → setIsAuthenticated(true) → effects run,
    // but they see isLoginRedirectHandled() === true and skip.
    setLoginRedirectHandled(true);
    if (typeof window !== 'undefined') {
      // Store credentials for browser password manager (Firefox, Chrome, etc.)
      if (loginEmail && loginPassword && 'credentials' in navigator) {
        try {
          const w = window as any;
          if (w.PasswordCredential) {
            const cred = new w.PasswordCredential({
              id: loginEmail,
              password: loginPassword,
              name: response.firstName && response.lastName
                ? `${response.firstName} ${response.lastName}`
                : loginEmail,
            });
            (navigator as any).credentials.store(cred).catch((e) => console.error("[api]", e));
          }
        } catch {}
      }
      persistAuthStorage(response);
    }
    if (typeof window === 'undefined') return;
    const storedRedirect = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');
    const safeRedirect = storedRedirect && storedRedirect.startsWith('/') && !storedRedirect.startsWith('//')
      ? storedRedirect
      : '/';
    const target = response.mustChangePassword ? '/profile#password' : safeRedirect;
    // Use a full navigation after login. Besides resetting all pre-login client
    // state, this guarantees that Next middleware sees the freshly stored
    // HttpOnly refreshToken cookie before rendering the protected target.
    window.location.replace(target);
  };

  const getLocalizedError = (err: string | null) => {
    if (!err) return null;
    const lower = err.toLowerCase();
    // Unified message for bad credentials — prevents user enumeration
    if (lower.includes('password') || lower.includes('user not found') || lower.includes('bad credentials') || lower.includes('invalid credentials')) return t('invalidCredentials');
    if (lower.includes('verification code') || lower.includes('2fa')) return t('invalidVerificationCode');
    if (lower.includes('too many') || lower.includes('attempts') || lower.includes('p\u0159\xedli\u0161 mnoho') || lower.includes('zablokov\xe1n')) return t('tooManyFailedAttempts');
    return err;
  };

  const handleInvalidEmail = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.validity.valueMissing) {
      target.setCustomValidity(t('fillOutField'));
    } else if (target.validity.typeMismatch) {
      target.setCustomValidity(t('invalidEmailFormat'));
    } else {
      target.setCustomValidity('');
    }
  };

  const handleInvalidPassword = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.validity.valueMissing) {
      target.setCustomValidity(t('fillOutField'));
    } else if (target.validity.tooShort) {
      target.setCustomValidity(t('passwordTooShortHelp'));
    } else {
      target.setCustomValidity('');
    }
  };

  const handleInvalid2FA = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.validity.valueMissing) {
      target.setCustomValidity(t('fillOutField'));
    } else {
      target.setCustomValidity('');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    
    setError(null);
    setBusy(true);
    if (typeof window !== 'undefined' && email) {
      window.localStorage.setItem('lastLoginEmail', email);
    }
    try {
      if (twoFactorToken) {
        const body: VerifyTwoFactorRequest = { twoFactorToken, code: twoFactorCode };
        const response = await apiFetch<AuthResponse>('/api/auth/verify-2fa', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        finishLogin(response, email, password);
        return;
      }

      const body: LoginRequest = { email, password };
      const response = await apiFetch<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.requiresTwoFactor && response.twoFactorToken) {
        setTwoFactorToken(response.twoFactorToken);
      } else {
        finishLogin(response, email, password);
      }
    } catch (e: any) {
      const errorMessage = e?.message || t('loginError');
      setError(errorMessage);
      const apiErr = e as { remainingAttempts?: number; retryAfterSeconds?: number };

      if (
        errorMessage.toLowerCase().includes('p\u0159\xedli\u0161 mnoho') ||
        errorMessage.toLowerCase().includes('too many') ||
        e?.status === 429
      ) {
        // Rate-limited by backend — block for the duration returned by backend
        setCanSubmit(false);
        setRemainingAttempts(0);
        const blockMs = (apiErr.retryAfterSeconds && apiErr.retryAfterSeconds > 0)
          ? apiErr.retryAfterSeconds * 1000
          : 15 * 60 * 1000;
        setTimeout(() => {
          setCanSubmit(true);
          setRemainingAttempts(null);
        }, blockMs);
      } else if (typeof apiErr.remainingAttempts === 'number' && apiErr.remainingAttempts > 0) {
        // Not blocked yet, but running low on attempts
        setRemainingAttempts(apiErr.remainingAttempts);
      }
      // No cooldown for normal errors (wrong password, etc.) — let the
      // user retry immediately. `busy` already prevents double-submit.
    } finally {
      setBusy(false);
    }
  };

  // Show skeleton during hydration to avoid flash of empty form (email not
  // yet loaded from localStorage). This is very brief — just one render cycle.
  if (!mounted) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-start justify-center pt-8">
        <div className="w-full max-w-md">
          <div className="rounded-rustic border border-wheat/40 bg-linen p-6 space-y-4 animate-pulse">
            <div className="h-6 w-24 bg-wheat/30 rounded" />
            <div className="h-10 w-full bg-wheat/20 rounded" />
            <div className="h-10 w-full bg-wheat/20 rounded" />
            <div className="h-10 w-full bg-moss/20 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-start justify-center pt-8">
      <LoadingOverlay
        variant="login"
        label={twoFactorToken ? t('verifying') : t('loggingIn')}
        visible={busy}
      />
      <div className="w-full max-w-md">
      <Card>
        <form onSubmit={onSubmit} className="grid gap-4" autoComplete="on">
          <div>
            <label className="field-label" htmlFor="email">{t('email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              ref={emailInputRef}
              className={`field-input ${emailTouched && !isValidEmail ? 'field-input-invalid' : ''}`}
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailTouched(true);
              }}
              onBlur={() => setEmailTouched(true)}
              required
              onInvalid={handleInvalidEmail}
              onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
            />
            {emailTouched && !isValidEmail ? (
              <p className="field-error-text">{t('invalidEmailFormat')}</p>
            ) : (
              <p className="text-xs text-wood/80 mt-1 ">{t('emailHelp')}</p>
            )}
          </div>
          {!twoFactorToken && (
            <div>
              <label className="field-label" htmlFor="password">{t('password')}</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`field-input pr-10 ${passwordTouched && !isValidPassword ? 'field-input-invalid' : ''}`}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordTouched(true);
                  }}
                  onBlur={() => setPasswordTouched(true)}
                  required
                  minLength={6}
                  onInvalid={handleInvalidPassword}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                />
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const el = document.getElementById('password') as HTMLInputElement | null;
                    if (el && !password && el.value) { setPassword(el.value); setPasswordTouched(true); }
                    setShowPassword((v) => !v);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-wood hover:text-bark transition z-10"
                  title={showPassword ? t('hidePassword') : t('showPassword')}
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {passwordTouched && !isValidPassword ? (
                <p className="field-error-text">{t('passwordTooShortHelp')}</p>
              ) : (
                <p className="text-xs text-wood/80 mt-1 ">{t('passwordHelp')}</p>
              )}
            </div>
          )}
          {twoFactorToken && (
            <div>
              <label className="field-label" htmlFor="twoFactorCode">{t('verificationCode')}</label>
              <input
                id="twoFactorCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className={`field-input ${twoFactorCodeTouched && !isValidTwoFactorCode ? 'field-input-invalid' : ''}`}
                value={twoFactorCode}
                onChange={(e) => {
                  setTwoFactorCode(e.target.value.replace(/\D/g, ''));
                  setTwoFactorCodeTouched(true);
                }}
                onBlur={() => setTwoFactorCodeTouched(true)}
                required
                maxLength={6}
                placeholder="123456"
                onInvalid={handleInvalid2FA}
                onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
              />
              {twoFactorCodeTouched && !isValidTwoFactorCode && (
                <p className="field-error-text">{t('invalidVerificationCode')}</p>
              )}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || !canSubmit || !formValid}
          >
            {busy
              ? (twoFactorToken ? t('verifying') : t('loggingIn'))
              : !canSubmit
                ? t('pleaseWait')
                : (twoFactorToken ? t('verifyAndLogin') : t('loginButton'))
            }
          </button>
          {!twoFactorToken && (
            <div className="text-center">
              <Link
                href="/forgot-password"
                className="text-sm text-moss hover:text-moss-dark underline transition"
              >
                {t('forgotPasswordLink')}
              </Link>
            </div>
          )}
          {twoFactorToken && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setTwoFactorToken(null);
                  setTwoFactorCode('');
                  setTwoFactorCodeTouched(false);
                  setError(null);
                }}
                className="text-sm text-moss hover:text-moss-dark underline transition"
              >
                {t('backToLogin')}
              </button>
            </div>
          )}
          {error && <Alert type="error">{getLocalizedError(error)}</Alert>}
          {remainingAttempts !== null && remainingAttempts > 0 && (
            <p className="text-center text-sm text-wood">
              {t('attemptsRemaining').replace('{count}', String(remainingAttempts))}
            </p>
          )}
        </form>
      </Card>
      </div>
    </div>
  );
}
